import { Injectable, inject, signal } from "@angular/core"
import { Product } from "../meta/Product.interface"
import { AvailabilityService } from "./availability.service"
import { BehaviorSubject, combineLatest } from "rxjs"
import { BillingPosition } from "../meta/BillingPosition.interface"
import { PhoneBookService } from "./phone-book.service"
import { MonthlyDiscount } from "../meta/MonthlyDiscount.interface"
import { BusinessPortingService } from "./business-porting.service"

@Injectable({
  providedIn: "root"
})
export class ProductsService {
  private readonly availabilityService = inject(AvailabilityService)
  private readonly businessPortingService = inject(BusinessPortingService)
  private readonly phonebookService = inject(PhoneBookService)

  private readonly _availableProducts$ = new BehaviorSubject<Product[]>([])
  public readonly availableProducts$ = this._availableProducts$.asObservable()

  private readonly _variantContractTimeBaseProducts$ = new BehaviorSubject<Product[]>([])

  private readonly _baseProduct$ = new BehaviorSubject<Product | null>(null)
  public readonly baseProduct$ = this._baseProduct$.asObservable()

  private readonly _productOptions$ = new BehaviorSubject<Product[]>([])
  public readonly productOptions$ = this._productOptions$.asObservable()

  private readonly _totalStartFee$ = new BehaviorSubject<number>(0)
  public readonly totalStartFee$ = this._totalStartFee$.asObservable()

  private readonly _totalMonthlyFee$ = new BehaviorSubject<number>(0)
  public readonly totalMonthlyFee$ = this._totalMonthlyFee$.asObservable()

  private readonly _totalMonthlyDiscountedFee$ = new BehaviorSubject<number>(0)
  public readonly totalMonthlyDiscountedFee$ = this._totalMonthlyDiscountedFee$.asObservable()

  private readonly _cpeProduct$ = new BehaviorSubject<Product | null>(null)
  public readonly cpeProduct$ = this._cpeProduct$.asObservable()

  private readonly _couponProduct$ = new BehaviorSubject<Product | null>(null)
  public readonly couponProduct$ = this._couponProduct$.asObservable()

  private readonly _hazProducts$ = new BehaviorSubject<Product[]>([])

  public billingPositions = signal<BillingPosition[]>([])

  public monthlyDiscounts = signal<MonthlyDiscount[]>([])

  constructor() {
    this.availabilityService.availability$.subscribe((availability) => {
      if (availability) {
        this._availableProducts$.next(availability.availableProducts
          .filter((product) =>
            product.marketingInformation.contractTime === 0
          )
        )
        this._variantContractTimeBaseProducts$.next(availability.availableProducts
          .filter((product) =>
            product.marketingInformation.contractTime > 0
          )          
        )
        this._hazProducts$.next(availability.hazProducts)
      } else {
        this._availableProducts$.next([])
        this._hazProducts$.next([])
      }
      this.setBaseProduct(null)
    })
    combineLatest([this._baseProduct$, this.businessPortingService.businessPorting$]).subscribe(([baseProduct, portingData]) => {
      if (baseProduct) {
        if (portingData && portingData.porting) {
          this.addPortingProduct()
        } else {
          this.remPortingProduct()
        }
      }
    })
    combineLatest([this._baseProduct$, this._productOptions$]).subscribe(([baseProduct, productOptions]) => {
      this.updateBillingPositions(baseProduct, productOptions)
      this.updateCpeProduct(baseProduct, productOptions)
    })
    combineLatest([this._baseProduct$, this.phonebookService.phoneBookData$]).subscribe(([baseProduct, phoneBookData]) => {
      if (baseProduct) {
        if (phoneBookData && phoneBookData.evn) {
          this.setEvnProduct(phoneBookData.evn)
        } else {
          this.setEvnProduct("none")
        }
      }
    })
    this._baseProduct$.subscribe((baseProduct) => {
      this._couponProduct$.next(null)
    })
  }

  public getBaseProduct(): Product | null {
    return this._baseProduct$.getValue()
  }

  public setBaseProduct(product: Product | null): void {
    this.setProductOptions([])
    this._baseProduct$.next(product)
    if (product && !this._cpeProduct$.getValue()) {
      const availableFreeCpeProducts = this.getOptionalProducts().filter((product) => this.hasLabel(product, "CPE") && product.marketingInformation?.recurringMonthlyFee === 0)
      if (availableFreeCpeProducts.length > 0) {
        this.addProductOption(availableFreeCpeProducts[0])
      }
    }
    if (product) {
      const hazOptions = this.getOptionalProducts().filter((product) => this.hasLabel(product, "Option") && this.hasLabel(product, "HAZ"))
      const availableHazIDs = this._hazProducts$.value.map((product) => product.productId)
      const forcedHazOptions = hazOptions.filter((product) => availableHazIDs.indexOf(product.productId) >= 0)
      if (forcedHazOptions.length > 0) {
        this.addProductOption(forcedHazOptions[0])
      }
    }
  }

  private setProductOptions(products: Product[]): void {
    this._productOptions$.next(products)
  }

  public addProductOption(product: Product): void {
    const duplicate = this._productOptions$
      .getValue()
      .find((existingProduct) => existingProduct.productId === product.productId)
    if (!duplicate) {
      this.setProductOptions([...this._productOptions$.getValue(), product])
    }
  }

  public remProductOption(product: Product): void {
    this.setProductOptions([...this._productOptions$.getValue()].filter((productOption) => productOption.productId !== product.productId))
  }

  private updateCpeProduct(baseProduct: Product | null, productOptions: Product[]): void {
    if (!baseProduct) {
      this._cpeProduct$.next(null)
      return
    }
    const selectedCpeProducts = productOptions.filter((product) => this.hasLabel(product, "CPE"))
    if (selectedCpeProducts.length > 0) {
      this._cpeProduct$.next(selectedCpeProducts[0])
    } else {
      this._cpeProduct$.next(null)
    }
  }

  private updateBillingPositions(baseProduct: Product | null, productOptions: Product[]): void {
    if (!baseProduct) {
      this.billingPositions.set([])
      this._totalStartFee$.next(0)
      this._totalMonthlyFee$.next(0)
      this._totalMonthlyDiscountedFee$.next(0)
      return
    }
    const billingPositions: BillingPosition[] = [{ name: baseProduct.name, sum: baseProduct.marketingInformation.recurringMonthlyFee, type: "monthlyDiscounted" }]
    const monthlyDiscounts: MonthlyDiscount[] = []
    let monthlySum = 0
    let monthlySumDiscounted = baseProduct.marketingInformation.recurringMonthlyFee
    let startFeeSum = 0
    baseProduct.includedProducts.forEach((includedProduct) => {
      if (includedProduct.hasOwnProperty("marketingInformation") && typeof includedProduct.marketingInformation.recurringMonthlyFee === "number" && includedProduct.marketingInformation.recurringMonthlyFee !== 0) {
        billingPositions.push({ name: includedProduct.name, sum: includedProduct.marketingInformation.recurringMonthlyFee, type: "monthly" })
        monthlySum += includedProduct.marketingInformation.recurringMonthlyFee
      }
      if (includedProduct.hasOwnProperty("marketingInformation") && typeof includedProduct.marketingInformation.startFee === "number" && includedProduct.marketingInformation.startFee !== 0) {
        billingPositions.push({ name: includedProduct.name, sum: includedProduct.marketingInformation.startFee, type: "startFee" })
        startFeeSum += includedProduct.marketingInformation.startFee
      }
    })
    if (monthlySum === 0) {
      monthlySum = monthlySumDiscounted
    } else {
      monthlyDiscounts.push({
        months: baseProduct.discountMonths,
        monthAfter: baseProduct.discountMonths + 1,
        discount: monthlySumDiscounted - monthlySum,
        sumAfterDiscounts: 0,
        baseFeeAfterDiscounts: 0,
        type: "base"
      })
    }
    const baseProductUndiscountedFee = monthlySum
    productOptions.forEach((productOption) => {
      if (
        productOption.hasOwnProperty("marketingInformation")
        && typeof productOption.marketingInformation.recurringMonthlyFee === "number"
        && (
          productOption.marketingInformation.recurringMonthlyFee !== 0
          || this.hasLabel(productOption, "CPE")
          || this.hasLabel(productOption, "Portierung")
        )
      ) {
        billingPositions.push({ name: productOption.name, sum: productOption.marketingInformation.recurringMonthlyFee, type: "monthlyOption" })
        if (productOption.marketingInformation.recurringMonthlyFee > 0) {
          monthlySum += productOption.marketingInformation.recurringMonthlyFee
        } else if (productOption.marketingInformation.recurringMonthlyFee < 0) {
          const matchingMonthlyDiscount = monthlyDiscounts.find((monthlyDiscount) => monthlyDiscount.months === productOption.discountMonths)
          if (matchingMonthlyDiscount) {
            matchingMonthlyDiscount.discount += productOption.marketingInformation.recurringMonthlyFee
            matchingMonthlyDiscount.type = "baseCoupon"
          } else {
            monthlyDiscounts.push({
              months: productOption.discountMonths,
              monthAfter: productOption.discountMonths + 1,
              discount: productOption.marketingInformation.recurringMonthlyFee,
              sumAfterDiscounts: 0,
              baseFeeAfterDiscounts: 0,
              type: "baseCoupon"
            })
          }
        }
        monthlySumDiscounted += productOption.marketingInformation.recurringMonthlyFee
      }
      if (
        (productOption.hasOwnProperty("marketingInformation") && typeof productOption.marketingInformation.startFee === "number" && productOption.marketingInformation.startFee !== 0)
        || (this.hasLabel(productOption, "Option") && this.hasLabel(productOption, "HAZ"))
      ) {
        billingPositions.push({ name: productOption.name, sum: productOption.marketingInformation.startFee, type: "startFee" })
        startFeeSum += productOption.marketingInformation.startFee
      }
    })
    monthlyDiscounts.sort((mDa, mDb) => mDb.months - mDa.months)
    monthlyDiscounts.forEach((mD) => {
      mD.sumAfterDiscounts = monthlySum
      mD.baseFeeAfterDiscounts = baseProductUndiscountedFee
      const laterDiscounts = monthlyDiscounts.filter((lMd) => lMd.months > mD.months || lMd.months === 0)
      laterDiscounts.forEach((lD) => {
        mD.sumAfterDiscounts += lD.discount
        mD.baseFeeAfterDiscounts += lD.discount
      })
      if (mD.months === 0) {
        monthlySum += mD.discount
      }
    })
    monthlyDiscounts.reverse()
    this.monthlyDiscounts.set(monthlyDiscounts)
    this._totalMonthlyFee$.next(monthlySum)
    this._totalMonthlyDiscountedFee$.next(monthlySumDiscounted)
    this._totalStartFee$.next(startFeeSum)
    this.billingPositions.set(billingPositions)
  }

  private getOptionalProducts(): Product[] {
    const baseProduct = this._baseProduct$.getValue()
    if (!baseProduct || !Array.isArray(baseProduct.optionalProducts)) {
      return []
    }
    return baseProduct.optionalProducts
  }

  public getChoosableProductOptions(): Product[] {
    return this.getOptionalProducts().filter((product) =>
      !this.hasLabel(product, "EVN")
      && !this.hasLabel(product, "Portierung")
      && !this.hasLabel(product, "Mypages")
      && !this.hasLabel(product, "CPE")
      && !this.hasLabel(product, "Coupon")
      && !this.hasLabel(product, "HAZ")
    )
  }

  public getChoosableCpeOptions(): Product[] {
    return this.getOptionalProducts().filter((product) =>
      this.hasLabel(product, "CPE")
      && product.marketingInformation.recurringMonthlyFee > 0
    )
  }

  public getCustomerCpeOption(): Product {
    return this.getOptionalProducts().find((product) =>
      this.hasLabel(product, "CPE")
      && product.marketingInformation.recurringMonthlyFee === 0
    )!
  }

  public getProductOptions(): Product[] {
    return this._productOptions$.getValue()
  }

  public isOptionSelected(product: Product): boolean {
    return (this.getProductOptions().filter((productOption) => productOption.productId === product.productId).length > 0)
  }

  public getOrderProducts(): { productId: number }[] {
    return [{ productId: this._baseProduct$.getValue()!.productId }]
    .concat(this.getProductOptions()
      .map((product) => ({ productId: product.productId }))
    )
  }

  private hasLabel(product: Product, label: string) {
    if (Array.isArray(product.labels) && product.labels.filter((productLabel) => productLabel === label).length > 0) {
      return true
    }
    return false
  }

  public isOptionDisabled(product: Product): boolean {
    if (this.hasLabel(product, "CPE")) {
      const selectedCpes = this._productOptions$.getValue().filter((productOption) => this.hasLabel(productOption, "CPE"))
      if (selectedCpes.filter((cpe) => cpe.productId !== product.productId).length > 0) {
        return true
      }
    }
    return false
  }

  public getMatchingVariantContractTimeBaseProducts(product: Product): Product[] {
    return this._variantContractTimeBaseProducts$.getValue().filter((variantProduct) =>
      variantProduct.marketingInformation.speedDown === product.marketingInformation.speedDown
      && variantProduct.marketingInformation.speedUp === product.marketingInformation.speedUp
    )
  }

  private addPortingProduct(): void {
    const portingProduct = this.getOptionalProducts().find((product) => this.hasLabel(product, "Portierung"))
    if (portingProduct) {
      this.addProductOption(portingProduct)
    }
  }

  public setCouponProduct(couponProduct: Product): void {
    this._couponProduct$.next(couponProduct)
    this.addProductOption(couponProduct)
  }

  private remPortingProduct(): void {
    const portingProduct = this.getProductOptions().find((product) => this.hasLabel(product, "Portierung"))
    if (portingProduct) {
      this.remProductOption(portingProduct)
    }
  }

  private setEvnProduct(type: "none" | "short" | "full"): void {
    const oldEvnOptions = this.getProductOptions().filter((product) => this.hasLabel(product, "EVN"))
    oldEvnOptions.forEach((oldEvnOption) => {
      this.remProductOption(oldEvnOption)
    })
    const newEvnOption = this.getOptionalProducts().find((product) => this.hasLabel(product, "EVN") && this.hasLabel(product, type))
    if (newEvnOption) {
      this.addProductOption(newEvnOption)
    }
  }

  public getTotalMonthlyDiscountedFee(): number {
    return this._totalMonthlyDiscountedFee$.getValue()
  }

  public getTotalMonthlyFee(): number {
    return this._totalMonthlyFee$.getValue()
  }

  public getTotalStartFee(): number {
    return this._totalStartFee$.getValue()
  }

  public getCpeProduct(): Product | null {
    return this._cpeProduct$.getValue()
  }

  public hasMobileFlat(): boolean {
    const baseProduct = this.getBaseProduct()
    if (baseProduct) {
      return baseProduct.includedProducts.concat(this.getProductOptions()).filter((product) =>
        product.name.toLocaleLowerCase().includes("mobil")
        && product.name.toLocaleLowerCase().includes("flat")
      ).length > 0
    }
    return false
  }

}
